import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import Breadcrumbs from '../components/Breadcrumbs';
import LoadingIndicator from '../components/LoadingIndicator';
import EmptyState from '../components/EmptyState';
import { searchProducts } from '../services/product.api';
import { trackEvent, type AnalyticsEventParams } from '../services/analytics';
import { Product, Category, County, Location } from '../services/api.types';
import { fetchCategories } from '../services/category.api';
import { searchLocations } from '../services/location.api';
import { fetchCounties, forgetCachedCounties, isUnknownCountyError, readCachedCounties } from '../services/countyCache';
import { useTranslations } from '../translations';
import { createPageUrl } from '../utils/appRoutes';
import { getPaginationItems } from '../utils/pagination';
import { ArrowUpDown, ChevronDown, CircleHelp, Filter, Grid2X2, House, List, RotateCcw, SearchX } from 'lucide-react';
import './SearchResults.css';
import '../components/ThemedDropdown.css';

type SearchAction = AnalyticsEventParams['search_results_viewed']['search_action'];

interface SearchResultsProps {
  isLoggedIn: boolean;
  searchParams: {
    query?: string;
    location?: string;
    locationId?: string;
    categoryId?: string;
    subCategoryId?: string;
    productType?: 'PRODUCT' | 'DEMAND';
  };
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToProductDetail: (productId: string) => void;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
  onNavigateToSearchRankingHelp?: () => void;
}

type NegotiationFilter = '' | 'NEGOTIABLE' | 'NON_NEGOTIABLE';

interface MobileProductFilters {
  categoryId: string;
  subCategoryId: string;
  minPrice: string;
  maxPrice: string;
  negotiation: NegotiationFilter;
  seller: string;
}

export const countActiveMobileProductFilters = ({
  categoryId,
  subCategoryId,
  minPrice,
  maxPrice,
  negotiation,
  seller,
}: MobileProductFilters) => [
  Boolean(categoryId),
  Boolean(subCategoryId),
  Boolean(minPrice || maxPrice),
  Boolean(negotiation),
  Boolean(seller.trim()),
].filter(Boolean).length;

const SearchResults: React.FC<SearchResultsProps> = ({
  isLoggedIn,
  searchParams,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProductDetail,
  onLogout,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
  onNavigateToSearchRankingHelp,
}) => {
  const t = useTranslations();

  // Initialize from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const urlNegotiationType = urlParams.get('negotiationType');
  const initialNegotiationFilter: NegotiationFilter =
    urlNegotiationType === 'NEGOTIABLE' || urlNegotiationType === 'NON_NEGOTIABLE' ? urlNegotiationType : '';

  const [searchQuery, setSearchQuery] = useState(urlParams.get('query') || '');
  const [location, setLocation] = useState(urlParams.get('location') || '');
  const [selectedLocationId, setSelectedLocationId] = useState(urlParams.get('locationId') || searchParams.locationId || '');
  const [distance, setDistance] = useState(urlParams.get('distance') || '');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [viewType, setViewType] = useState<'grid' | 'list'>('list');
  const [productType, setProductType] = useState<'PRODUCT' | 'DEMAND'>((urlParams.get('productType') as 'PRODUCT' | 'DEMAND') || searchParams.productType || 'PRODUCT');
  const [sortOrder, setSortOrder] = useState('Cele mai noi');

  const getSortParams = () => {
    switch (sortOrder) {
      case 'Cele mai vechi':
        return { sortField: 'PUBLISHED_AT', order: 'ASC' as const };
      case 'Pret crescator':
        return { sortField: 'PRICE', order: 'ASC' as const };
      case 'Pret descrescator':
        return { sortField: 'PRICE', order: 'DESC' as const };
      case 'Cele mai noi':
      default:
        return { sortField: 'PUBLISHED_AT', order: 'DESC' as const };
    }
  };

  const getSortLabel = () => {
    switch (sortOrder) {
      case 'Cele mai vechi':
        return t.searchResults.oldest;
      case 'Pret crescator':
        return t.searchResults.priceAscending;
      case 'Pret descrescator':
        return t.searchResults.priceDescending;
      case 'Cele mai noi':
      default:
        return t.searchResults.newest;
    }
  };

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(urlParams.get('categoryId') || searchParams.categoryId || '');
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(urlParams.get('subCategoryId') || searchParams.subCategoryId || '');
  const [minPrice, setMinPrice] = useState(urlParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(urlParams.get('maxPrice') || '');
  const [negotiationFilter, setNegotiationFilter] = useState<NegotiationFilter>(initialNegotiationFilter);
  const [seller, setSeller] = useState(urlParams.get('seller') || '');

  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showDistanceDropdown, setShowDistanceDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubCategoryDropdown, setShowSubCategoryDropdown] = useState(false);
  const [showNegotiationDropdown, setShowNegotiationDropdown] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [counties, setCounties] = useState<County[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const locationInputRef = useRef<HTMLDivElement>(null);
  const locationRequestIdRef = useRef(0);
  const distanceDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const subCategoryDropdownRef = useRef<HTMLDivElement>(null);
  const negotiationDropdownRef = useRef<HTMLDivElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [countySearchQuery, setCountySearchQuery] = useState('');
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [searchingWholeCounty, setSearchingWholeCounty] = useState(false);
  const isInitialMount = useRef(true);
  const searchRequestIdRef = useRef(0);
  // Label for the next effect-driven load; consumed by that load.
  const searchActionRef = useRef<SearchAction>('initial');

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);
  const categoryBreadcrumb = selectedCategory ? selectedCategory.name : t.searchResults.allCategories;
  const subCategoryBreadcrumb = selectedCategory?.subCategories.find(sc => sc.id === selectedSubCategoryId)?.name || '';
  const negotiationFilterLabel =
    negotiationFilter === 'NEGOTIABLE'
      ? t.productDetail.negotiable
      : negotiationFilter === 'NON_NEGOTIABLE'
        ? t.productDetail.nonNegotiable
        : t.common.all;

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data.content);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      // The load effect below runs in this same commit and performs the initial search.
      isInitialMount.current = false;
      // Preload counties (don't show dropdown)
      loadCounties(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showMobileFilters) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileFilters]);

  useEffect(() => {
    // Blur search button when loading starts
    if (loading && searchButtonRef.current) {
      searchButtonRef.current.blur();
    }
  }, [loading]);

  useEffect(() => {
    if (!isInitialMount.current) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const action = searchActionRef.current;
      searchActionRef.current = 'filter';
      loadProducts(false, action);
      updateUrlParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortOrder, productType]);

  useEffect(() => {
    if (!isInitialMount.current) {
      updateUrlParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId, selectedSubCategoryId, searchQuery, selectedLocationId, distance, minPrice, maxPrice, negotiationFilter, seller]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (locationInputRef.current && !locationInputRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (location.length >= 3 && !selectedLocation && !searchingWholeCounty && !selectedLocationId) {
      const debounceTimer = setTimeout(() => {
        loadLocationsByName(location);
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
  }, [location, selectedLocation, searchingWholeCounty, selectedLocationId]);

  const updateUrlParams = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('query', searchQuery);
    if (location) params.set('location', location);
    if (selectedLocationId) params.set('locationId', selectedLocationId);
    if (selectedCategoryId) params.set('categoryId', selectedCategoryId);
    if (selectedSubCategoryId) params.set('subCategoryId', selectedSubCategoryId);
    if (productType) params.set('productType', productType);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (distance) params.set('distance', distance);
    if (seller) params.set('seller', seller);
    if (negotiationFilter) params.set('negotiationType', negotiationFilter);

    window.history.replaceState(
      {},
      '',
      createPageUrl('search', Object.fromEntries(params.entries()))
    );
  };

  const loadProducts = async (withoutFilters = false, action: SearchAction = 'filter') => {
    const requestId = ++searchRequestIdRef.current;
    setLoading(true);
    try {
      const subCategoryIds = !withoutFilters && selectedSubCategoryId ? [selectedSubCategoryId] : [];
      const minPriceNum = !withoutFilters && minPrice ? parseFloat(minPrice) : null;
      const maxPriceNum = !withoutFilters && maxPrice ? parseFloat(maxPrice) : null;
      const negType = !withoutFilters ? negotiationFilter || null : null;
      const { sortField, order } = getSortParams();

      let locationParam: any = undefined;

      if (!withoutFilters && selectedLocationId) {
        locationParam = { ids: [selectedLocationId] };

        const range = Number(distance);
        if (range > 0) {
          locationParam.range = {
            range
          };
        }
      }

      const data = await searchProducts({
        page: withoutFilters ? 1 : currentPage,
        size: 18,
        sortField,
        order,
        productType: productType,
        name: !withoutFilters ? searchQuery || null : null,
        location: locationParam,
        categoryId: !withoutFilters ? selectedCategoryId || null : null,
        subCategoryIds: subCategoryIds,
        minPrice: minPriceNum,
        maxPrice: maxPriceNum,
        negotiationType: negType,
        username: !withoutFilters ? seller || null : null,
      });

      if (requestId !== searchRequestIdRef.current) return;
      setProducts(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      trackEvent('search_results_viewed', {
        result_count: data.totalElements,
        zero_results: data.totalElements === 0,
        search_action: action,
        has_query: !withoutFilters && searchQuery.trim() !== '',
        category_id: withoutFilters ? undefined : selectedCategoryId || undefined,
        county_code: withoutFilters || !selectedLocationId
          ? undefined
          : (selectedCounty ?? selectedLocation?.county)?.name,
        listing_type: productType === 'DEMAND' ? 'wanted' : 'sale',
      });
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      if (requestId === searchRequestIdRef.current) setLoading(false);
    }
  };

  const handleSearch = () => {
    // A page reset re-runs the load effect; label that (winning) request too.
    if (currentPage !== 1) searchActionRef.current = 'submit';
    setCurrentPage(1);
    loadProducts(false, 'submit');
    updateUrlParams();
  };

  const handlePageChange = (page: number) => {
    if (page !== currentPage) searchActionRef.current = 'paginate';
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (distanceDropdownRef.current && !distanceDropdownRef.current.contains(event.target as Node)) {
        setShowDistanceDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (subCategoryDropdownRef.current && !subCategoryDropdownRef.current.contains(event.target as Node)) {
        setShowSubCategoryDropdown(false);
      }
      if (negotiationDropdownRef.current && !negotiationDropdownRef.current.contains(event.target as Node)) {
        setShowNegotiationDropdown(false);
      }
    };

    if (showDistanceDropdown || showSortDropdown || showCategoryDropdown || showSubCategoryDropdown || showNegotiationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDistanceDropdown, showSortDropdown, showCategoryDropdown, showSubCategoryDropdown, showNegotiationDropdown]);

  const renderPagination = () => {
    const pages = getPaginationItems(currentPage, totalPages);
    const compactPages = getPaginationItems(currentPage, totalPages, true);

    const renderPages = (items: ReturnType<typeof getPaginationItems>) => items.map((page) => (
      typeof page !== 'number' ? (
        <span key={page} className="product-search-result-pagination-ellipsis">...</span>
      ) : (
        <button
          type="button"
          key={page}
          className={`product-search-result-pagination-number ${currentPage === page ? 'product-search-result-active' : ''}`}
          onClick={() => handlePageChange(page)}
          aria-current={currentPage === page ? 'page' : undefined}
          aria-label={`Pagina ${page}`}
        >
          {page}
        </button>
      )
    ));

    return (
      <div className="product-search-result-pagination">
        <button
          type="button"
          className="product-search-result-pagination-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Pagina anterioară"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.0005 4.99993C14.2265 4.99993 14.4535 5.07593 14.6405 5.23193C15.0645 5.58493 15.1215 6.21593 14.7685 6.63993L10.2925 12.0109L14.6075 17.3729C14.9535 17.8029 14.8855 18.4329 14.4555 18.7789C14.0245 19.1249 13.3955 19.0569 13.0485 18.6269L8.22051 12.6269C7.92251 12.2559 7.92651 11.7259 8.23151 11.3599L13.2315 5.35993C13.4295 5.12293 13.7135 4.99993 14.0005 4.99993Z" fill="currentColor"/>
          </svg>
        </button>

        <div className="product-search-result-pagination-pages product-search-result-pagination-pages-desktop">
          {renderPages(pages)}
        </div>
        <div className="product-search-result-pagination-pages product-search-result-pagination-pages-mobile">
          {renderPages(compactPages)}
        </div>

        <button
          type="button"
          className="product-search-result-pagination-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Pagina următoare"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.99951 19.0001C9.77351 19.0001 9.54651 18.9241 9.35951 18.7681C8.93551 18.4151 8.87851 17.7841 9.23151 17.3601L13.7075 11.9891L9.39251 6.62708C9.04651 6.19708 9.11451 5.56708 9.54451 5.22108C9.97551 4.87508 10.6045 4.94308 10.9515 5.37308L15.7795 11.3731C16.0775 11.7441 16.0735 12.2741 15.7685 12.6401L10.7685 18.6401C10.5705 18.8771 10.2865 19.0001 9.99951 19.0001Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    );
  };

  const loadCounties = async (shouldShowDropdown = true) => {
    const requestId = ++locationRequestIdRef.current;
    const showCounties = (loadedCounties: County[]) => {
      if (requestId !== locationRequestIdRef.current) {
        return;
      }
      setCounties(loadedCounties);
      setLocations([]);
      setSelectedCounty(null);
      if (shouldShowDropdown) {
        setShowLocationDropdown(true);
      }
    };

    const cachedCounties = readCachedCounties();
    if (cachedCounties) {
      showCounties(cachedCounties);
      return;
    }

    setLocationLoading(true);
    try {
      showCounties(await fetchCounties());
    } catch (error) {
      console.error('Error fetching counties:', error);
    } finally {
      if (requestId === locationRequestIdRef.current) {
        setLocationLoading(false);
      }
    }
  };

  const loadLocationsByCounty = async (county: County) => {
    const requestId = ++locationRequestIdRef.current;
    setLocationLoading(true);
    setSelectedCounty(county);
    try {
      const data = await searchLocations(1, 500, county.id, null);
      if (requestId === locationRequestIdRef.current) {
        setLocations(data.locations.content);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      if (isUnknownCountyError(error)) {
        // The cached county list is from an older database: drop it and show the current one.
        forgetCachedCounties();
        if (requestId === locationRequestIdRef.current) {
          void loadCounties();
          return;
        }
      }
      if (requestId === locationRequestIdRef.current) {
        setLocations([]);
      }
    } finally {
      if (requestId === locationRequestIdRef.current) {
        setLocationLoading(false);
      }
    }
  };

  const loadLocationsByName = async (searchName: string) => {
    const requestId = ++locationRequestIdRef.current;
    setLocationLoading(true);
    try {
      const data = await searchLocations(1, 500, null, searchName);
      if (requestId === locationRequestIdRef.current) {
        setCounties([]);
        setLocations(data.locations.content);
        setSelectedCounty(null);
        setShowLocationDropdown(true);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      if (requestId === locationRequestIdRef.current) {
        setLocationLoading(false);
      }
    }
  };

  const handleLocationInputFocus = () => {
    if (selectedLocationId || searchingWholeCounty) {
      return;
    }
    if (location.length < 3) {
      if (counties.length > 0) {
        // Counties already loaded, just show dropdown
        setShowLocationDropdown(true);
      } else {
        // Load counties and show dropdown
        loadCounties();
      }
    }
  };

  const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    locationRequestIdRef.current += 1;
    setLocation(value);
    setSelectedLocation(null);
    setSelectedCounty(null);
    setSelectedLocationId('');
    setSearchingWholeCounty(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');

    if (value.length === 0) {
      setShowLocationDropdown(true);
      loadCounties();
    } else if (value.length < 3) {
      setShowLocationDropdown(false);
    }
  };

  const handleCountySelect = (county: County) => {
    loadLocationsByCounty(county);
  };

  const handleLocationSelect = (locationItem: Location) => {
    locationRequestIdRef.current += 1;
    setSelectedLocation(locationItem);
    setSelectedLocationId(locationItem.id);
    setLocation(`${locationItem.diacriticsName}, ${locationItem.county.name}`);
    setShowLocationDropdown(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setSearchingWholeCounty(false);
  };

  const handleShowAllFromCounty = (county: County) => {
    locationRequestIdRef.current += 1;
    setSelectedCounty(county);
    setLocation(county.name);
    setSelectedLocation(null);
    setSelectedLocationId(county.id);
    setShowLocationDropdown(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setSearchingWholeCounty(true);
  };

  const handleCountySearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCountySearchQuery(value);

    if (value.length === 0 && !selectedCounty) {
      loadCounties();
    }
  };

  const handleLocationSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocationSearchQuery(value);

    if (value.length === 0 && selectedCounty) {
      loadLocationsByCounty(selectedCounty);
    }
  };

  const filteredCounties = counties.filter(county =>
    county.name.toLowerCase().includes(countySearchQuery.trim().toLowerCase())
  );

  const filteredLocations = locations.filter(loc => {
    const searchLower = locationSearchQuery.trim().toLowerCase();
    return loc.name.toLowerCase().includes(searchLower) ||
           loc.diacriticsName.toLowerCase().includes(searchLower);
  });

  // Check if any filters are active (different from default values)
  const hasActiveFilters =
    selectedCategoryId !== '' ||
    selectedSubCategoryId !== '' ||
    minPrice !== '' ||
    maxPrice !== '' ||
    negotiationFilter !== '' ||
    seller !== '' ||
    location !== '' ||
    distance !== '' ||
    searchQuery !== '';
  const activeMobileFilterCount = countActiveMobileProductFilters({
    categoryId: selectedCategoryId,
    subCategoryId: selectedSubCategoryId,
    minPrice,
    maxPrice,
    negotiation: negotiationFilter,
    seller,
  });

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedCategoryId('');
    setSelectedSubCategoryId('');
    setMinPrice('');
    setMaxPrice('');
    setNegotiationFilter('');
    setSeller('');
    setLocation('');
    setSelectedLocationId('');
    setSelectedLocation(null);
    setSelectedCounty(null);
    setSearchingWholeCounty(false);
    setDistance('');
  };

  const handleResetEmptySearch = () => {
    handleResetFilters();
    setCurrentPage(1);
    void loadProducts(true);
    window.history.replaceState(
      {},
      '',
      createPageUrl('search', productType ? { productType } : {})
    );
  };

  const handleClearLocation = () => {
    locationRequestIdRef.current += 1;
    setLocation('');
    setSelectedLocationId('');
    setCounties([]);
    setLocations([]);
    setSelectedLocation(null);
    setSelectedCounty(null);
    setSearchingWholeCounty(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setShowLocationDropdown(false);
    setLocationLoading(false);
  };

  const handleClearDistance = () => {
    setDistance('');
  };

  return (
    <div className="product-search-result-page">
      <Header
        isLoggedIn={isLoggedIn}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="product-search-result-content">
        <div className="product-search-result-search-bar-section">
          <div className="product-search-result-search-bar-container">
            <div className="product-search-result-search-input-wrapper">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17.5 17.5L13.875 13.875" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                placeholder={t.home.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="product-search-result-search-input"
              />
              {searchQuery && (
                <button
                  className="product-search-result-clear-button"
                  onClick={() => setSearchQuery('')}
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>

            <div className="product-search-result-location-input-wrapper" ref={locationInputRef}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M0 5.28133C0 2.36867 2.39267 0 5.33333 0C8.274 0 10.6667 2.36867 10.6667 5.28133C10.6667 8.93133 5.96733 13.0007 5.76733 13.172C5.642 13.2793 5.488 13.3333 5.33333 13.3333C5.17867 13.3333 5.02467 13.2793 4.89933 13.172C4.69933 13.0007 0 8.93133 0 5.28133ZM5.33333 11.764C4.21667 10.708 1.33333 7.74333 1.33333 5.28133C1.33333 3.10467 3.12733 1.33333 5.33333 1.33333C7.53933 1.33333 9.33333 3.10467 9.33333 5.28133C9.33333 7.74333 6.45 10.708 5.33333 11.764ZM3 4.99967C3 3.713 4.04667 2.66633 5.33333 2.66633C6.62 2.66633 7.66667 3.713 7.66667 4.99967C7.66667 6.28633 6.62 7.333 5.33333 7.333C4.04667 7.333 3 6.28633 3 4.99967ZM4.33333 4.99967C4.33333 5.551 4.782 5.99967 5.33333 5.99967C5.88467 5.99967 6.33333 5.551 6.33333 4.99967C6.33333 4.44833 5.88467 3.99967 5.33333 3.99967C4.782 3.99967 4.33333 4.44833 4.33333 4.99967Z" fill="#071105" transform="translate(5, 2)"/>
              </svg>
              <input
                type="text"
                placeholder={t.searchResults.allCountry}
                value={location}
                onChange={handleLocationInputChange}
                onFocus={handleLocationInputFocus}
                className="product-search-result-location-input"
              />
              {location && (
                <button
                  className="product-search-result-clear-button"
                  onClick={handleClearLocation}
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              <button
                className="product-search-result-location-dropdown-toggle"
                onClick={() => {
                  if (showLocationDropdown) {
                    setShowLocationDropdown(false);
                  } else if (counties.length > 0 || locations.length > 0) {
                    setShowLocationDropdown(true);
                  } else {
                    loadCounties();
                  }
                }}
                type="button"
              >
                <svg className={`product-search-result-dropdown-icon themed-dropdown-chevron ${showLocationDropdown ? 'product-search-result-dropdown-icon-open themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                </svg>
              </button>

              {showLocationDropdown && (
                <div className="product-search-result-location-dropdown themed-dropdown-menu">
                    {locationLoading ? (
                      <div className="product-search-result-location-dropdown-loading">Loading...</div>
                    ) : (
                      <>
                        {counties.length > 0 && !selectedCounty && (
                        <div className="product-search-result-location-dropdown-list">
                          <div className="product-search-result-location-dropdown-search">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 14L11.1 11.1" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <input
                              type="text"
                              placeholder={t.searchResults.searchCounty}
                              value={countySearchQuery}
                              onChange={handleCountySearchChange}
                              className="product-search-result-location-search-input"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {filteredCounties.map((county) => (
                            <div
                              key={county.id}
                              className="product-search-result-location-dropdown-item county-item themed-dropdown-option"
                              onClick={() => handleCountySelect(county)}
                            >
                              {county.name}
                            </div>
                          ))}
                          {filteredCounties.length === 0 && (
                            <div className="product-search-result-location-dropdown-empty">Nu s-au găsit rezultate</div>
                          )}
                        </div>
                      )}

                      {selectedCounty && (
                        <>
                          <div className="product-search-result-location-dropdown-counties">
                            {counties.map((county) => (
                              <div
                                key={county.id}
                                className={`product-search-result-location-dropdown-item county-item themed-dropdown-option ${selectedCounty.id === county.id ? 'product-search-result-selected themed-dropdown-option-active' : ''}`}
                                onClick={() => handleCountySelect(county)}
                              >
                                {county.name}
                              </div>
                            ))}
                          </div>

                          {locations.length > 0 && (
                            <div className="product-search-result-location-dropdown-cities">
                              <div className="product-search-result-location-dropdown-header">
                                <div
                                  className="product-search-result-location-dropdown-show-all themed-dropdown-option"
                                  onClick={() => handleShowAllFromCounty(selectedCounty)}
                                >
                                  Arată tot din {selectedCounty.name}
                                </div>
                                <div className="product-search-result-location-dropdown-search">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M14 14L11.1 11.1" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  <input
                                    type="text"
                                    placeholder={t.searchResults.searchLocation}
                                    value={locationSearchQuery}
                                    onChange={handleLocationSearchChange}
                                    className="product-search-result-location-search-input"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                              {filteredLocations.map((locationItem) => (
                                <div
                                  key={locationItem.id}
                                  className={`product-search-result-location-dropdown-item themed-dropdown-option ${selectedLocation?.id === locationItem.id ? 'themed-dropdown-option-active' : ''}`}
                                  onClick={() => handleLocationSelect(locationItem)}
                                >
                                  {locationItem.diacriticsName}
                                </div>
                              ))}
                              {filteredLocations.length === 0 && (
                                <div className="product-search-result-location-dropdown-empty">Nu s-au găsit rezultate</div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {!selectedCounty && locations.length > 0 && (
                        <div className="product-search-result-location-dropdown-list">
                          {locations.map((locationItem) => (
                            <div
                              key={locationItem.id}
                              className={`product-search-result-location-dropdown-item themed-dropdown-option ${selectedLocation?.id === locationItem.id ? 'themed-dropdown-option-active' : ''}`}
                              onClick={() => handleLocationSelect(locationItem)}
                            >
                              {locationItem.diacriticsName}, {locationItem.county.name}
                            </div>
                          ))}
                        </div>
                      )}

                        {!locationLoading && counties.length === 0 && locations.length === 0 && !selectedLocation && !searchingWholeCounty && location.length >= 3 && (
                          <div className="product-search-result-location-dropdown-empty">Nu am găsit rezultate</div>
                        )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="product-search-result-distance-dropdown" ref={distanceDropdownRef}>
              <input
                type="number"
                min="1"
                className="product-search-result-distance-select"
                placeholder={t.searchResults.distance}
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                onFocus={() => setShowDistanceDropdown(true)}
              />
              {distance && (
                <button
                  className="product-search-result-distance-clear-button"
                  onClick={handleClearDistance}
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              <span className="product-search-result-distance-unit">km</span>
              <button
                className="product-search-result-distance-dropdown-toggle"
                onClick={() => setShowDistanceDropdown(!showDistanceDropdown)}
                type="button"
              >
                <svg className={`product-search-result-dropdown-icon themed-dropdown-chevron ${showDistanceDropdown ? 'product-search-result-dropdown-icon-open themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                </svg>
              </button>
              {showDistanceDropdown && (
                <div className="product-search-result-distance-dropdown-menu themed-dropdown-menu">
                    {['1', '5', '10', '15', '20', '30', '50', '75', '100'].map(km => (
                      <button key={km} className={`product-search-result-distance-option themed-dropdown-option ${distance === km ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setDistance(km); setShowDistanceDropdown(false); }}>
                        {km} km
                      </button>
                  ))}
                </div>
              )}
            </div>

            <button
              ref={searchButtonRef}
              className={`product-search-result-search-button ${loading ? 'loading' : ''}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={handleSearch}
            >
              {t.searchResults.searchButton}
            </button>
          </div>
        </div>

        <Breadcrumbs
          className="product-search-result-breadcrumbs"
          homeLabel={t.common.home}
          navigationLabel={t.common.navigation}
          onNavigateHome={onNavigateToHome}
          items={[
            {
              label: categoryBreadcrumb,
              onClick: subCategoryBreadcrumb ? () => setSelectedSubCategoryId('') : undefined
            },
            ...(subCategoryBreadcrumb ? [{ label: subCategoryBreadcrumb }] : [])
          ]}
        />

        <div className="product-search-result-tabs-section">
          <div className="product-search-result-tabs">
            <button
              className={`product-search-result-tab ${productType === 'PRODUCT' ? 'product-search-result-active' : ''}`}
              onClick={() => setProductType('PRODUCT')}
            >
              {t.searchResults.tabSell}
            </button>
            <button
              className={`product-search-result-tab ${productType === 'DEMAND' ? 'product-search-result-active' : ''}`}
              onClick={() => setProductType('DEMAND')}
            >
              {t.searchResults.tabBuy}
            </button>
          </div>
        </div>

        <div className="product-search-result-results-header">
          <div className="product-search-result-results-title-row">
            <div className="product-search-result-results-copy">
              <span className="product-search-result-results-eyebrow">
                {t.searchResults.resultsEyebrow}
              </span>
              <h1 className="product-search-result-results-title">
                {totalElements === 1
                  ? t.searchResults.resultFoundSingular.replace('{count}', totalElements.toString())
                  : t.searchResults.resultsFoundPlural.replace('{count}', totalElements.toString())
                }
              </h1>
              <p className="product-search-result-results-subtitle">
                {t.searchResults.resultsSubtitle}
              </p>
            </div>
            <button
              className="product-search-result-mobile-filters-button"
              type="button"
              aria-controls="product-search-result-filters"
              aria-expanded={showMobileFilters}
              onClick={() => setShowMobileFilters(true)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5 5.83333H7.5M7.5 5.83333C7.5 7.214 8.61929 8.33333 10 8.33333C11.3807 8.33333 12.5 7.214 12.5 5.83333M7.5 5.83333C7.5 4.45267 8.61929 3.33333 10 3.33333C11.3807 3.33333 12.5 4.45267 12.5 5.83333M12.5 5.83333H17.5M2.5 14.1667H7.5M7.5 14.1667C7.5 15.5473 8.61929 16.6667 10 16.6667C11.3807 16.6667 12.5 15.5473 12.5 14.1667M7.5 14.1667C7.5 12.786 8.61929 11.6667 10 11.6667C11.3807 11.6667 12.5 12.786 12.5 14.1667M12.5 14.1667H17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>{t.searchResults.filters}</span>
              {activeMobileFilterCount > 0 && (
                <span
                  className="product-search-result-mobile-filter-count"
                  aria-label={t.searchResults.activeFilters.replace(
                    '{count}', activeMobileFilterCount.toString()
                  )}
                >
                  {activeMobileFilterCount}
                </span>
              )}
            </button>
          </div>

          <div className="product-search-result-results-controls">
            <div className="product-search-result-sort-control">
              <div className="product-search-result-control-heading">
                <span className="product-search-result-control-label">{t.searchResults.orderBy}</span>
                <button
                  type="button"
                  className="product-search-result-ranking-help"
                  onClick={onNavigateToSearchRankingHelp}
                  aria-label={t.searchResults.rankingExplanationLink}
                  title={t.searchResults.rankingExplanationLink}
                >
                  <CircleHelp size={17} aria-hidden="true" />
                </button>
              </div>
              <div className="product-search-result-dropdown-wrapper" ref={sortDropdownRef}>
                <button
                  className="product-search-result-dropdown-select"
                  onClick={() => setShowSortDropdown((isOpen) => !isOpen)}
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={showSortDropdown}
                >
                  <ArrowUpDown className="product-search-result-sort-icon" size={16} aria-hidden="true" />
                  <span>{getSortLabel()}</span>
                  <ChevronDown
                    className={`product-search-result-dropdown-icon themed-dropdown-chevron ${showSortDropdown ? 'product-search-result-dropdown-icon-open themed-dropdown-chevron-open' : ''}`}
                    size={18}
                    aria-hidden="true"
                  />
                </button>
                {showSortDropdown && (
                  <div className="product-search-result-sort-dropdown-menu themed-dropdown-menu" role="listbox">
                    <button type="button" role="option" aria-selected={sortOrder === 'Cele mai noi'} className={`product-search-result-sort-option themed-dropdown-option ${sortOrder === 'Cele mai noi' ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setSortOrder('Cele mai noi'); setShowSortDropdown(false); }}>
                      {t.searchResults.newest}
                    </button>
                    <button type="button" role="option" aria-selected={sortOrder === 'Cele mai vechi'} className={`product-search-result-sort-option themed-dropdown-option ${sortOrder === 'Cele mai vechi' ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setSortOrder('Cele mai vechi'); setShowSortDropdown(false); }}>
                      {t.searchResults.oldest}
                    </button>
                    <button type="button" role="option" aria-selected={sortOrder === 'Pret crescator'} className={`product-search-result-sort-option themed-dropdown-option ${sortOrder === 'Pret crescator' ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setSortOrder('Pret crescator'); setShowSortDropdown(false); }}>
                      {t.searchResults.priceAscending}
                    </button>
                    <button type="button" role="option" aria-selected={sortOrder === 'Pret descrescator'} className={`product-search-result-sort-option themed-dropdown-option ${sortOrder === 'Pret descrescator' ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setSortOrder('Pret descrescator'); setShowSortDropdown(false); }}>
                      {t.searchResults.priceDescending}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="product-search-result-view-control">
              <span className="product-search-result-control-label">
                {t.searchResults.viewType}
              </span>
              <div className="product-search-result-view-toggle" role="group" aria-label={t.searchResults.viewType}>
                <button
                  className={`product-search-result-view-button ${viewType === 'list' ? 'product-search-result-active' : ''}`}
                  onClick={() => setViewType('list')}
                  type="button"
                  aria-label={t.searchResults.listView}
                  aria-pressed={viewType === 'list'}
                >
                  <List size={18} aria-hidden="true" />
                </button>
                <button
                  className={`product-search-result-view-button ${viewType === 'grid' ? 'product-search-result-active' : ''}`}
                  onClick={() => setViewType('grid')}
                  type="button"
                  aria-label={t.searchResults.gridView}
                  aria-pressed={viewType === 'grid'}
                >
                  <Grid2X2 size={17} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="product-search-result-results-container">
          {showMobileFilters && (
            <div
              className="product-search-result-mobile-filters-overlay"
              onClick={() => setShowMobileFilters(false)}
            />
          )}
          <aside
            id="product-search-result-filters"
            className={`product-search-result-filters-sidebar ${showMobileFilters ? 'product-search-result-mobile-visible' : ''}`}
          >
            <div className="product-search-result-mobile-filters-header">
              <h2 className="product-search-result-mobile-filters-title">Filtre</h2>
              <button
                className="product-search-result-mobile-filters-close"
                onClick={() => setShowMobileFilters(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="product-search-result-filter-group">
              <label className="product-search-result-filter-label">Categorie</label>
              <div className="product-search-result-dropdown-wrapper" ref={categoryDropdownRef}>
                <button
                  className={`product-search-result-filter-select ${!selectedCategoryId ? 'product-search-result-filter-select-full-label' : ''}`}
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                >
                  <span>{selectedCategoryId ? categories.find(c => c.id === selectedCategoryId)?.name : t.searchResults.allCategories}</span>
                </button>
                <button
                  className="product-search-result-dropdown-toggle-button"
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  type="button"
                >
                  <svg className={`product-search-result-dropdown-icon themed-dropdown-chevron ${showCategoryDropdown ? 'product-search-result-dropdown-icon-open themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                  </svg>
                </button>
                {showCategoryDropdown && (
                  <div className="product-search-result-filter-dropdown-menu themed-dropdown-menu">
                    <button className={`product-search-result-filter-option themed-dropdown-option ${!selectedCategoryId ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setSelectedCategoryId(''); setSelectedSubCategoryId(''); setShowCategoryDropdown(false); }}>
                      {t.searchResults.allCategories}
                    </button>
                    {categories.map(cat => (
                      <button key={cat.id} className={`product-search-result-filter-option themed-dropdown-option ${selectedCategoryId === cat.id ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setSelectedCategoryId(cat.id); setSelectedSubCategoryId(''); setShowCategoryDropdown(false); }}>
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="product-search-result-filter-group">
              <label className="product-search-result-filter-label">{t.searchResults.filterSubcategory}</label>
              <div className="product-search-result-dropdown-wrapper" ref={subCategoryDropdownRef}>
                <button
                  className="product-search-result-filter-select"
                  onClick={() => {
                    if (selectedCategory && selectedCategory.subCategories.length > 0) {
                      setShowSubCategoryDropdown(!showSubCategoryDropdown);
                    }
                  }}
                  disabled={!selectedCategory || selectedCategory.subCategories.length === 0}
                >
                  <span>{selectedSubCategoryId ? selectedCategory?.subCategories.find(sc => sc.id === selectedSubCategoryId)?.name : ''}</span>
                </button>
                <button
                  className="product-search-result-dropdown-toggle-button"
                  onClick={() => {
                    if (selectedCategory && selectedCategory.subCategories.length > 0) {
                      setShowSubCategoryDropdown(!showSubCategoryDropdown);
                    }
                  }}
                  disabled={!selectedCategory || selectedCategory.subCategories.length === 0}
                  type="button"
                >
                  <svg className={`product-search-result-dropdown-icon themed-dropdown-chevron ${showSubCategoryDropdown ? 'product-search-result-dropdown-icon-open themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                  </svg>
                </button>
                {showSubCategoryDropdown && selectedCategory && (
                  <div className="product-search-result-filter-dropdown-menu themed-dropdown-menu">
                    <button className={`product-search-result-filter-option themed-dropdown-option ${!selectedSubCategoryId ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setSelectedSubCategoryId(''); setShowSubCategoryDropdown(false); }}>
                      {t.searchResults.allSubcategories}
                    </button>
                    {selectedCategory.subCategories.map(subCat => (
                      <button key={subCat.id} className={`product-search-result-filter-option themed-dropdown-option ${selectedSubCategoryId === subCat.id ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setSelectedSubCategoryId(subCat.id); setShowSubCategoryDropdown(false); }}>
                        {subCat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="product-search-result-filter-group">
              <label className="product-search-result-filter-label">{t.searchResults.filterPriceRange}</label>
              <div className="product-search-result-price-range-vertical">
                <div className="product-search-result-price-field">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="product-search-result-price-input-full"
                    placeholder={t.searchResults.filterPriceMin}
                  />
                </div>
                <div className="product-search-result-price-field">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="product-search-result-price-input-full"
                    placeholder={t.searchResults.filterPriceMax}
                  />
                </div>
              </div>

              <div className="product-search-result-negotiation-field">
                <label className="product-search-result-filter-label">{t.searchResults.filterNegotiable}</label>
                <div className="product-search-result-dropdown-wrapper" ref={negotiationDropdownRef}>
                  <button
                    type="button"
                    className={`product-search-result-filter-select ${!negotiationFilter ? 'product-search-result-filter-select-full-label' : ''}`}
                    onClick={() => setShowNegotiationDropdown(!showNegotiationDropdown)}
                  >
                    <span>{negotiationFilterLabel}</span>
                  </button>
                  <button
                    className="product-search-result-dropdown-toggle-button"
                    onClick={() => setShowNegotiationDropdown(!showNegotiationDropdown)}
                    type="button"
                  >
                    <svg className={`product-search-result-dropdown-icon themed-dropdown-chevron ${showNegotiationDropdown ? 'product-search-result-dropdown-icon-open themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                    </svg>
                  </button>
                  {showNegotiationDropdown && (
                    <div className="product-search-result-filter-dropdown-menu themed-dropdown-menu">
                      <button className={`product-search-result-filter-option themed-dropdown-option ${!negotiationFilter ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setNegotiationFilter(''); setShowNegotiationDropdown(false); }}>
                        {t.common.all}
                      </button>
                      <button className={`product-search-result-filter-option themed-dropdown-option ${negotiationFilter === 'NEGOTIABLE' ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setNegotiationFilter('NEGOTIABLE'); setShowNegotiationDropdown(false); }}>
                        {t.productDetail.negotiable}
                      </button>
                      <button className={`product-search-result-filter-option themed-dropdown-option ${negotiationFilter === 'NON_NEGOTIABLE' ? 'themed-dropdown-option-active' : ''}`} onClick={() => { setNegotiationFilter('NON_NEGOTIABLE'); setShowNegotiationDropdown(false); }}>
                        {t.productDetail.nonNegotiable}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="product-search-result-filter-group">
              <label className="product-search-result-filter-label">{t.searchResults.filterSeller}</label>
              <input
                type="text"
                value={seller}
                onChange={(e) => setSeller(e.target.value)}
                className="product-search-result-price-input-full"
                placeholder={t.searchResults.sellerPlaceholder}
              />
            </div>

            <div className="product-search-result-mobile-filter-actions">
              <button
                className={`product-search-result-mobile-apply-button ${loading ? 'loading' : ''}`}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={() => {
                  setShowMobileFilters(false);
                  handleSearch();
                }}
              >
                <Filter size={17} aria-hidden />
                {t.searchResults.applyFilters}
              </button>
              <button
                className="product-search-result-reset-filters-button"
                type="button"
                onClick={handleResetFilters}
                disabled={!hasActiveFilters}
              >
                <RotateCcw size={17} aria-hidden />
                {t.searchResults.resetFilters}
              </button>
            </div>
          </aside>

          <div className={viewType === 'list' ? 'product-search-result-products-list' : 'product-search-result-products-grid'}>
            {loading ? (
              <LoadingIndicator message={t.searchResults.loading} />
            ) : products.length === 0 ? (
              <EmptyState
                className="product-search-result-empty-state"
                icon={<SearchX />}
                title={t.searchResults.emptyTitle}
                description={t.searchResults.emptyDescription}
                headingLevel={2}
                announce
              >
                {hasActiveFilters && (
                  <button
                    className="app-empty-state-primary"
                    type="button"
                    onClick={handleResetEmptySearch}
                  >
                    <RotateCcw size={17} aria-hidden="true" />
                    {t.searchResults.resetFilters}
                  </button>
                )}
                <button
                  className={hasActiveFilters ? 'app-empty-state-secondary' : 'app-empty-state-primary'}
                  type="button"
                  onClick={onNavigateToHome}
                >
                  <House size={17} aria-hidden="true" />
                  {t.searchResults.homeAction}
                </button>
              </EmptyState>
            ) : (
              products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  viewType={viewType}
                  onNavigateToProductDetail={onNavigateToProductDetail}
                  isLoggedIn={isLoggedIn}
                  onNavigateToLogin={onNavigateToLogin}
                />
              ))
            )}
          </div>
        </div>

        {!loading && products.length > 0 && renderPagination()}
      </div>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

export default SearchResults;
